// src/firebase/firebase.js
import { initializeApp, FirebaseError } from 'firebase/app';
import {
    getAuth,
    GoogleAuthProvider,
    signInWithPopup,
    signOut,
    onAuthStateChanged,
    signInWithEmailAndPassword as firebaseSignInWithEmailAndPassword,
    connectAuthEmulator
} from 'firebase/auth';
import {
    getFirestore,
    doc,
    getDoc,
    setDoc,
    collection,
    query,
    where,
    getDocs,
    Timestamp,
    onSnapshot,
    deleteDoc,
    addDoc,
    runTransaction,
    orderBy,
    connectFirestoreEmulator
} from 'firebase/firestore';
import firebaseConfig from '../config/firebaseConfig'; // Import your config

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const googleProvider = new GoogleAuthProvider();

// Connect to emulators in development
if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_EMULATORS === 'true') {
    try {
        connectAuthEmulator(auth, 'http://localhost:9099');
        connectFirestoreEmulator(db, 'localhost', 8080);
        console.log('Connected to Firebase emulators');
    } catch (error) {
        console.log('Emulators already connected or failed to connect:', error);
    }
}

// Function to save or update user profile in Firestore ---
const saveUserProfile = async (user) => {
    if (!user) return;

    const userRef = doc(db, 'users', user.uid);
    try {
        const docSnap = await getDoc(userRef);
        if (!docSnap.exists()) {
            // User profile doesn't exist, create it
            await setDoc(userRef, {
                uid: user.uid,
                email: user.email,
                displayName: user.displayName,
                createdAt: Timestamp.now(),
                lastLoginAt: Timestamp.now(),
                // Optionally add other default fields like 'role', 'sections'
            });
            console.log("New user profile created in Firestore:", user.uid);
        } else {
            // User profile exists, update last login time
            await setDoc(userRef, {
                lastLoginAt: Timestamp.now(),
            }, { merge: true });
            console.log("Existing user profile updated in Firestore:", user.uid);
        }
    } catch (error) {
        console.error("Error saving user profile to Firestore:", error);
    }
};

// --- Wrap signInWithPopup to also save user profile ---
const signInWithGoogle = async () => {
    try {
        const result = await signInWithPopup(auth, googleProvider);
        await saveUserProfile(result.user); // Save user profile after successful sign-in
        return result;
    } catch (error) {
        console.error("Google Sign-In Error:", error);
        throw error; // Re-throw to be caught by the AuthScreen component
    }
};

// --- NEW: Wrap signInWithEmailAndPassword to also save user profile ---
const signInWithEmailAndPassword = async (authInstance, email, password) => {
    try {
        const result = await firebaseSignInWithEmailAndPassword(authInstance, email, password);
        await saveUserProfile(result.user); // Save user profile after successful sign-in
        return result;
    } catch (error) {
        console.error("Email/Password Sign-In Error:", error);
        throw error; // Re-throw to be caught by the AuthScreen component
    }
};

export {
    app,
    auth,
    db,
    googleProvider,
    signInWithPopup,
    signOut,
    onAuthStateChanged,
    doc,
    getDoc,
    setDoc,
    collection,
    query,
    where,
    getDocs,
    Timestamp,
    onSnapshot,
    deleteDoc,
    addDoc,
    signInWithGoogle,
    signInWithEmailAndPassword,
    runTransaction,
    orderBy,
    FirebaseError
};
